import { createReducer, on } from '@ngrx/store';
import { CamerasActions } from './cameras.action-types';
import { SortDirection } from '@angular/material/sort';
import { variableMenuLevel2Validation } from '@consts/menu.const';
import { Camera, CameraProfileInterface, EdgeCamera, FileCamera, ScanType, SearchDevicesByIpRangeData, Substream } from '../../../cameras/camera.model';
import { CamerasCreateType } from '@enums/cameras.enum';
import { CapabilityType } from '@models/search-configuration.model';

export declare interface CamerasState {
  edgeId: string;
  locationId: string;
  cameras: Camera[];
  totalItemsCount: number;
  page: number;
  perPage: number;
  orderBy: string;
  orderDirection: SortDirection;
  query: string;

  //method tab
  selectedCameraCreateType: CamerasCreateType;
  selectedCamera: Camera;
  cameraDetails: EdgeCamera.CameraDocument;
  snapshotUrl: string;
  // Multiple Cameras
  selectedCameras: Camera[];
  camerasDetails: EdgeCamera.CameraDocument[];
  snapshotUrls: string[];
  cameraProfiles?: CameraProfileInterface[];
  isOnvif: boolean;
  scanData: SearchDevicesByIpRangeData;
  scanType: ScanType;

  createEnabled: boolean;

  file: Partial<File>;
  listFromFile: FileCamera[];

  level2MenuValid: { [key: string]: boolean };
  camerasAutoComplete: { _id: string; name: string }[];
  substreams: Substream[];
  isWebrtc: boolean;
  isWebrtcRelay: boolean;
  cameraGetDetailsLoading: boolean;
}

// TODO: REMOVE - FOR DEV PURPOSES
export const initialState: CamerasState = {
  edgeId: null,
  locationId: null,
  cameras: null,
  totalItemsCount: 0,
  page: 0,
  perPage: 10,
  orderBy: 'name',
  orderDirection: 'asc',
  query: null,
  scanData: null,
  scanType: ScanType.Network,
  //method tab

  selectedCameraCreateType: CamerasCreateType.scan,
  selectedCamera: null,
  cameraDetails: null,
  snapshotUrl: null,
  // Multiple cameras
  selectedCameras: [],
  camerasDetails: [],
  snapshotUrls: [],

  cameraProfiles: null,
  isOnvif: true,

  createEnabled: true,

  file: null,
  listFromFile: null,
  substreams: [],

  level2MenuValid: variableMenuLevel2Validation,
  camerasAutoComplete: [],
  isWebrtc: true,
  isWebrtcRelay: false,
  cameraGetDetailsLoading: false,
};

export const camerasStateReducer = createReducer(
  initialState,
  on(CamerasActions.resetToInitialState, state => {
    return {
      ...initialState,
      cameras: state.cameras,
    };
  }),
  on(CamerasActions.resetToScanOptions, state => {
    return {
      ...initialState,
      edgeId: state.edgeId,
      locationId: state.locationId,
    };
  }),
  on(CamerasActions.resetConnectionData, state => {
    return {
      ...initialState,
      edgeId: state.edgeId,
      locationId: state.locationId,
      selectedCameraCreateType: state.selectedCameraCreateType,
      selectedCamera: state.selectedCamera,
      cameras: state.cameras,
      scanData: state.scanData,
    };
  }),
  on(CamerasActions.resetCameraDetails, state => {
    return {
      ...initialState,
      cameraDetails: null,
      snapshotUrl: null,
    };
  }),
  on(CamerasActions.setCameras, (state, { cameras }) => {
    return {
      ...state,
      cameras,
    };
  }),
  on(CamerasActions.rescan, (state) => {
    return {
      ...state,
      cameras: [],
    };
  }),
  on(CamerasActions.pushCameras, (state, { cameras }) => {
    return {
      ...state,
      cameras: cameras.concat(state.cameras),
    };
  }),
  on(CamerasActions.setFile, (state, { file }) => {
    return {
      ...state,
      file,
    };
  }),
  on(CamerasActions.setIsWebrtc, (state, { isWebrtc }) => {
    return {
      ...state,
      isWebrtc,
    };
  }),
  on(CamerasActions.setIsWebrtcRelay, (state, { isWebrtcRelay }) => {
    return {
      ...state,
      isWebrtcRelay,
    };
  }),
  on(CamerasActions.setListFromFile, (state, { listFromFile }) => {
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.setCreateEnabled, (state, { createEnabled }) => {
    return {
      ...state,
      createEnabled,
    };
  }),
  on(CamerasActions.setCameraSession, (state, { index, session, reset = true }) => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    if (reset) {
      listFromFile[index] = {
        ...listFromFile[index],
        session,
        loading: true,
        success: false,
        error: false,
      };
    } else {
      listFromFile[index] = {
        ...listFromFile[index],
        session,
      };
    }
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.resetCamersSessions, state => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    return {
      ...state,
      listFromFile,
      snapshotUrl: undefined,
      loading: true,
      success: false,
      error: false,
      errorMsg: undefined,
    };
  }),
  on(CamerasActions.replaceCameraSession, (state, { oldSession, newSession }) => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    const idx = listFromFile.findIndex(camera => camera.session === oldSession);
    listFromFile[idx] = {
      ...listFromFile[idx],
      session: newSession,
    };
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.setCameraSessionStatus, (state, { session, error, errorMsg, success, loading }) => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    const idx = listFromFile.findIndex(camera => camera.session === session);
    listFromFile[idx] = {
      ...listFromFile[idx],
      loading: loading ?? false,
      error: error ?? false,
      errorMsg: errorMsg ?? null,
      success: success ?? false,
    };
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.setCameraCreateStatus, (state, { session, createStatus, createErrorMsg }) => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    const idx = listFromFile?.findIndex(camera => camera?.session === session);
    listFromFile[idx] = {
      ...listFromFile[idx],
      createStatus,
      createErrorMsg,
    };
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.setMultiCameraData, (state, { session, snapshotUrl, camera }) => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    const idx = listFromFile.findIndex(camera => camera.session === session);
    listFromFile[idx] = {
      ...listFromFile[idx],
      snapshotUrl,
      camera,
    };
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.updateMultiCamera, (state, { session, camera }) => {
    const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
    const idx = listFromFile.findIndex(camera => camera.session === session);
    listFromFile[idx] = {
      ...listFromFile[idx],
      ...camera,
    };
    return {
      ...state,
      listFromFile,
    };
  }),
  on(CamerasActions.setIsOnvif, (state, { isOnvif }) => {
    return {
      ...state,
      isOnvif,
    };
  }),
  on(CamerasActions.setOrderParams, (state, { orderBy, orderDirection }) => {
    switch (state.selectedCameraCreateType) {
      case CamerasCreateType.onvif:
        const cameras = [...state.cameras];
        cameras.sort((a, b) => {
          const o1 = a[orderBy]?.toString()
            .toLowerCase();
          const o2 = b[orderBy]?.toString()
            .toLowerCase();
          return orderDirection === 'asc' ? o1.localeCompare(o2) : o2.localeCompare(o1);
        });
        return {
          ...state,
          orderBy,
          orderDirection,
          cameras,
        };
        break;

      case CamerasCreateType.file:
        const listFromFile = !!state.listFromFile ? [...state.listFromFile] : [];
        listFromFile.sort((a, b) => {
          return orderDirection === 'asc' ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
        });
        return {
          ...state,
          orderBy,
          orderDirection,
          listFromFile,
        };
        break;
      case CamerasCreateType.manual:
      default:
        return {
          ...state,
          orderBy,
          orderDirection,
        };
    }
  }),
  on(CamerasActions.setTotalItemsCount, (state, { totalItemsCount }) => {
    return {
      ...state,
      totalItemsCount,
    };
  }),
  on(CamerasActions.setCameraCreateType, (state, { selectedCameraCreateType }) => {
    return {
      ...state,
      selectedCameraCreateType,
    };
  }),
  on(CamerasActions.addValidMenuPoint, (state, { point }) => {
    return {
      ...state,
      level2MenuValid: {
        ...state.level2MenuValid,
        [point]: true,
      },
    };
  }),
  on(CamerasActions.rmValidMenuPoint, (state, { point }) => {
    return {
      ...state,
      level2MenuValid: {
        ...state.level2MenuValid,
        [point]: false,
      },
    };
  }),
  on(CamerasActions.setQuery, (state, { query }) => {
    return {
      ...state,
      query,
    };
  }),
  on(CamerasActions.setEdgeId, (state, { edgeId }) => {
    return {
      ...state,
      edgeId,
      cameras: edgeId !== state.edgeId ? null : state.cameras,
    };
  }),
  on(CamerasActions.setCameraProfiles, (state, { cameraProfiles }) => {
    return {
      ...state,
      cameraProfiles,
    };
  }),
  on(CamerasActions.setScanData, (state, { scanData }) => {
    return {
      ...state,
      scanData,
    };
  }),
  on(CamerasActions.setSelectedCameraStreamCapabilities, (state, { token, index, streamCapabilities }) => {
    const selectedCameras: Camera[] = [...state.selectedCameras];
    const idx = index || index === 0 ? index : selectedCameras.findIndex(camera => camera?.addState?.token === token);
    selectedCameras[idx] = {
      ...selectedCameras[idx],
      addState: {
        ...selectedCameras[idx]?.addState,
        streamCapabilities,
      },
    };
    return {
      ...state,
      selectedCameras,
    };
  }),
  on(CamerasActions.setCameraDetails, (state, { cameraDetails, multiple }) => {
    if (multiple) {
      const selectedCameras: Camera[] = [...state.selectedCameras];
      const idx = selectedCameras.findIndex(camera => camera.cameraId === cameraDetails.cameraId);
      selectedCameras[idx] = {
        ...selectedCameras[idx],
        addState: {
          ...selectedCameras[idx]?.addState,
          cameraDetails,
        },
      };
      return {
        ...state,
        selectedCameras,
      };
    }
    return {
      ...state,
      cameraDetails,
    };
  }),
  on(CamerasActions.setName, (state, { name }) => {
    return {
      ...state,
      cameraDetails: {
        ...state.cameraDetails,
        name,
      },
    };
  }),
  on(CamerasActions.setDescription, (state, { description }) => {
    return {
      ...state,
      cameraDetails: {
        ...state.cameraDetails,
        description,
      },
    };
  }),
  on(CamerasActions.setAutoConfigure, (state, { autoConfigure }) => {
    return {
      ...state,
      cameraDetails: {
        ...state.cameraDetails,
        autoConfigure,
      },
    };
  }),
  on(CamerasActions.setMainStream, (state, { connectionString, height, width }) => {
    return {
      ...state,
      cameraDetails: {
        ...state.cameraDetails,
        mainStream: {
          ...state.cameraDetails.mainStream,
          main: {
            ...state.cameraDetails.mainStream.main,
            connectionString,
            height,
            width,
          },
        },
      },
    };
  }),
  on(CamerasActions.setSubStream, (state, { connectionString, height, width }) => {
    return {
      ...state,
      cameraDetails: {
        ...state.cameraDetails,
        mainStream: {
          ...state.cameraDetails.mainStream,
          mainScale: {
            ...state.cameraDetails.mainStream.main,
            connectionString,
            height,
            width,
          },
        },
      },
    };
  }),
  on(CamerasActions.setLocationId, (state, { locationId }) => {
    return {
      ...state,
      locationId,
    };
  }),

  on(CamerasActions.clearSelectedCamera, (state) => {
    return {
      ...state,
      selectedCamera: null,
      cameraDetails: null,
      snapshotUrl: null,
      cameraProfiles: null,

    };
  }),

  on(CamerasActions.setSelectedCamera, (state, { selectedCamera }) => {
    return {
      ...state,
      selectedCamera,
    };
  }),
  on(CamerasActions.setSelectedCameras, (state, { selectedCameras }) => {
    return {
      ...state,
      selectedCameras,
    };
  }),
  on(CamerasActions.setSnapshotUrl, (state, { snapshotUrl, multiple, token }) => {
    if (multiple) {
      const selectedCameras: Camera[] = [...state.selectedCameras];
      const idx = selectedCameras.findIndex(camera => camera?.addState?.token === token);
      selectedCameras[idx] = {
        ...selectedCameras[idx],
        addState: {
          ...selectedCameras[idx]?.addState,
          snapshot: snapshotUrl,
        },
      };
      return {
        ...state,
        selectedCameras,
      };
    }
    return {
      ...state,
      snapshotUrl,
    };
  }),
  on(CamerasActions.setPaginationParams, (state, { page, perPage }) => {
    return {
      ...state,
      page,
      perPage,
    };
  }),
  on(CamerasActions.getCamerasAutocompleteSuccess, (state, { camerasAutoComplete }) => {
    return {
      ...state,
      camerasAutoComplete,
    };
  }),
  on(CamerasActions.addSubstream, (state, {}) => {
    const substreams = [...state.substreams];
    substreams.push({
      connectionString: '',
      connected: false,
      loading: false,
    });
    return {
      ...state,
      substreams,
    };
  }),
  on(CamerasActions.updateSubstream, (state, { connectionString, index }) => {
    const substreams = [...state.substreams];
    substreams[index] = {
      ...substreams[index],
      connectionString,
    };
    return {
      ...state,
      substreams,
    };
  }),
  on(CamerasActions.deleteSubstream, (state, { index }) => {
    const substreams = [...state.substreams];
    substreams.splice(index, 1);
    return {
      ...state,
      substreams,
    };
  }),
  on(CamerasActions.testSubstream, (state, { index }) => {
    const substreams = [...state.substreams];
    substreams[index] = {
      ...substreams[index],
      loading: true,
    };
    return {
      ...state,
      substreams,
    };
  }),
  on(CamerasActions.setCameraError, (state, { cameraId, error }) => {
    const selectedCameras: Camera[] = [...state.selectedCameras];
    const idx = selectedCameras.findIndex(camera => camera.cameraId === cameraId);
    selectedCameras[idx] = {
      ...selectedCameras[idx],
      addState: {
        ...selectedCameras[idx]?.addState,
        error,
      },
    };
    return {
      ...state,
      selectedCameras,
    };
  }),
  on(CamerasActions.setSubstreamSession, (state, { index, session }) => {
    const substreams = [...state.substreams];
    substreams[index] = {
      ...substreams[index],
      session,
    };
    return {
      ...state,
      substreams,
    };
  }),
  on(CamerasActions.testSubstreamDone, (state, { session, success, deleteSub, width, height }) => {
    const substreams = [...state.substreams];
    const index = substreams.findIndex(sub => sub.session === session);

    if (deleteSub) {
      substreams.splice(index, 1);
    } else {
      substreams[index] = {
        ...substreams[index],
        loading: false,
        connected: success,
        width,
        height,
      };
    }
    return {
      ...state,
      substreams,
    };
  }),
  on(CamerasActions.setCameraGetDetailsLoading, (state, { loading }) => {
    return {
      ...state,
      cameraGetDetailsLoading: loading,
    };
  }),
);
